import { useState, useMemo, useCallback } from 'react';

/**
 * Central bulk-selection state used by every list page (Leads, Products,
 * Customers, Quotations). Keeps the mental model identical across the app:
 *  - Click a row's checkbox → toggle it
 *  - Header checkbox → select-all-visible / clear-all
 *  - Clearing filters or unmounting resets selection
 *
 * `allVisibleIds` is intentionally accepted every render so "select all"
 * reflects the CURRENT filter/search, not the raw dataset.
 */
export default function useBulkSelection(allVisibleIds = []) {
  const [selected, setSelected] = useState(() => new Set());

  const toggle = useCallback((id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);

  const toggleAll = useCallback(() => {
    setSelected(prev => {
      const visible = new Set(allVisibleIds);
      // If every visible row is already selected → clear those; else add them all.
      const allSelected = allVisibleIds.length > 0 && allVisibleIds.every(id => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        visible.forEach(id => next.delete(id));
        return next;
      }
      return new Set([...prev, ...visible]);
    });
  }, [allVisibleIds]);

  const selectedIds = useMemo(() => Array.from(selected), [selected]);
  const isAllSelected = allVisibleIds.length > 0 && allVisibleIds.every(id => selected.has(id));
  const isSomeSelected = selected.size > 0 && !isAllSelected;

  return {
    selected,
    selectedIds,
    count: selected.size,
    isSelected: (id) => selected.has(id),
    toggle,
    toggleAll,
    clear,
    isAllSelected,
    isSomeSelected,
  };
}
